import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { captureRefFromUrl } from "@/lib/referral";
import "@/App.css";
import HomePage from "@/pages/HomePage";
import ArticlePage from "@/pages/ArticlePage";
import CategoryPage from "@/pages/CategoryPage";
import LoginPage from "@/pages/admin/LoginPage";
import ContactPage from "@/pages/ContactPage";
import VideoBulletinPage from "@/pages/VideoBulletinPage";
import EditorPage from "@/pages/EditorPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import NewsListPage from "@/pages/admin/NewsListPage";
import NewsFormPage from "@/pages/admin/NewsFormPage";
import RetailersPage from "@/pages/admin/RetailersPage";
import StaffPage from "@/pages/admin/StaffPage";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import ContactMessagesPage from "@/pages/admin/ContactMessagesPage";
import AdsPage from "@/pages/admin/AdsPage";
import NewsletterPage from "@/pages/admin/NewsletterPage";
import SiteSettingsPage from "@/pages/admin/SiteSettingsPage";
import MandiPage from "@/pages/admin/MandiPage";
import EventsPage from "@/pages/admin/EventsPage";
import IDCardPage from "@/pages/admin/IDCardPage";
import CommentsPage from "@/pages/admin/CommentsPage";
import PushSenderPage from "@/pages/admin/PushSenderPage";
import OwnerIDCardPage from "@/pages/admin/OwnerIDCardPage";
import ClassifiedsAdminPage from "@/pages/admin/ClassifiedsAdminPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import LiveBlogAdminPage from "@/pages/admin/LiveBlogAdminPage";
import PollsAdminPage from "@/pages/admin/PollsAdminPage";
import WishesAdminPage from "@/pages/admin/WishesAdminPage";
import PdfAdsAdminPage from "@/pages/admin/PdfAdsAdminPage";
import ReporterApprovalsPage from "@/pages/admin/ReporterApprovalsPage";
import ReporterMapPage from "@/pages/admin/ReporterMapPage";
import ReporterRatingsPage from "@/pages/admin/ReporterRatingsPage";
import ReporterChatPage from "@/pages/admin/ReporterChatPage";
import SupportPage from "@/pages/SupportPage";
import PostAdPage from "@/pages/PostAdPage";
import SearchPage from "@/pages/SearchPage";
import ClassifiedsPublicPage from "@/pages/ClassifiedsPublicPage";
import JobsPage from "@/pages/JobsPage";
import RetailersPublicPage from "@/pages/RetailersPublicPage";
import LiveBlogPage from "@/pages/LiveBlogPage";
import SubscribePage from "@/pages/SubscribePage";
import BloodPage from "@/pages/BloodPage";
import SubmitWishPage from "@/pages/SubmitWishPage";
import { CGStatePage, CGDistrictPage, CGBlockPage } from "@/pages/CGPages";
import EPaperArchivePage from "@/pages/EPaperArchivePage";
import ReporterAppPage from "@/pages/ReporterAppPage";
import ReporterLeaderboardPage from "@/pages/ReporterLeaderboardPage";
import AqiHistoryPage from "@/pages/AqiHistoryPage";
import ContributorPage from "@/pages/ContributorPage";
import AboutPage from "@/pages/AboutPage";
import DisclaimerPage from "@/pages/DisclaimerPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ReporterRewardsPage from "@/pages/ReporterRewardsPage";
import ReporterCertVerifyPage from "@/pages/ReporterCertVerifyPage";
import BusinessDirectoryPage from "@/pages/BusinessDirectoryPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import ReferPage from "@/pages/ReferPage";
import DirectoryRevenuePage from "@/pages/admin/DirectoryRevenuePage";
import WhatsAppBroadcastPage from "@/pages/admin/WhatsAppBroadcastPage";
import AIImageGenPage from "@/pages/admin/AIImageGenPage";
import MobileBottomNav from "@/components/MobileBottomNav";
import BackToTopFab from "@/components/BackToTopFab";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import ContributorAdminPage from "@/pages/admin/ContributorAdminPage";
import EditorBytesAdminPage from "@/pages/admin/EditorBytesAdminPage";
import VideoEditorAdminPage from "@/pages/admin/VideoEditorAdminPage";
import PromoVideoPage from "@/pages/admin/PromoVideoPage";
import AutoNewsPage from "@/pages/admin/AutoNewsPage";
import { useAuth } from "@/context/AuthContext";

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">लोड हो रहा है…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  useEffect(() => { captureRefFromUrl(); }, []);
  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/article/:slug" element={<ArticlePage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/videos" element={<VideoBulletinPage />} />
        <Route path="/editor" element={<EditorPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/post-ad" element={<PostAdPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/classifieds" element={<ClassifiedsPublicPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/retailers" element={<RetailersPublicPage />} />
        <Route path="/live" element={<LiveBlogPage />} />
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="/blood" element={<BloodPage />} />
        <Route path="/wishes-submit" element={<SubmitWishPage />} />
        <Route path="/cg" element={<CGStatePage />} />
        <Route path="/cg/:district" element={<CGDistrictPage />} />
        <Route path="/cg/:district/:block" element={<CGBlockPage />} />
        <Route path="/epaper" element={<EPaperArchivePage />} />
        <Route path="/reporter" element={<ReporterAppPage />} />
        <Route path="/reporters" element={<ReporterLeaderboardPage />} />
        <Route path="/aqi" element={<AqiHistoryPage />} />
        <Route path="/contribute" element={<ContributorPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/reporter-rewards" element={<ReporterRewardsPage />} />
        <Route path="/verify-cert/:cid" element={<ReporterCertVerifyPage />} />
        <Route path="/directory" element={<BusinessDirectoryPage />} />
        <Route path="/biz/:slug" element={<BusinessProfilePage />} />
        <Route path="/refer" element={<ReferPage />} />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<DashboardPage />} />
          <Route path="news" element={<NewsListPage />} />
          <Route path="news/new" element={<NewsFormPage />} />
          <Route path="news/:id/edit" element={<NewsFormPage />} />
          <Route path="retailers" element={<RetailersPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="staff/:id/id-card" element={<IDCardPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="messages" element={<ContactMessagesPage />} />
          <Route path="ads" element={<AdsPage />} />
          <Route path="newsletter" element={<NewsletterPage />} />
          <Route path="settings" element={<SiteSettingsPage />} />
          <Route path="mandi" element={<MandiPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="comments" element={<CommentsPage />} />
          <Route path="push" element={<PushSenderPage />} />
          <Route path="owner-card" element={<OwnerIDCardPage />} />
          <Route path="classifieds" element={<ClassifiedsAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="directory-revenue" element={<DirectoryRevenuePage />} />
          <Route path="whatsapp-broadcast" element={<WhatsAppBroadcastPage />} />
          <Route path="ai-image" element={<AIImageGenPage />} />
          <Route path="live-blog" element={<LiveBlogAdminPage />} />
          <Route path="polls" element={<PollsAdminPage />} />
          <Route path="wishes" element={<WishesAdminPage />} />
          <Route path="pdf-ads" element={<PdfAdsAdminPage />} />
          <Route path="reporter-approvals" element={<ReporterApprovalsPage />} />
          <Route path="reporter-map" element={<ReporterMapPage />} />
          <Route path="reporter-ratings" element={<ReporterRatingsPage />} />
          <Route path="reporter-chat" element={<ReporterChatPage />} />
          <Route path="contributor" element={<ContributorAdminPage />} />
          <Route path="editor-bytes" element={<EditorBytesAdminPage />} />
          <Route path="video-editor" element={<VideoEditorAdminPage />} />
          <Route path="promo-video" element={<PromoVideoPage />} />
          <Route path="auto-news" element={<AutoNewsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MobileBottomNav />
      <BackToTopFab />
      <PWAInstallPrompt />
    </div>
  );
}
